import React, { createContext, useContext, useEffect, useState, useRef, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const ChatContext = createContext();

// Maps a notification's target link to its sidebar module path, so each module
// can show a "news" badge for unread notifications belonging to it.
const linkToModule = (link) => {
    if (!link) return null;
    if (link.startsWith('/chat')) return '/chat';
    if (link.startsWith('/services')) return '/services';
    if (link.startsWith('/events')) return '/events';
    if (link.startsWith('/carpooling')) return '/carpooling';
    if (link.startsWith('/news')) return '/news';
    if (link.startsWith('/tasks')) return '/tasks';
    if (link.startsWith('/bookings')) return '/bookings';
    if (link.startsWith('/documents')) return '/documents';
    if (link.startsWith('/facility')) return '/facility';
    if (link.startsWith('/meetings')) return '/meetings';
    if (link.startsWith('/my-children')) return '/my-children';
    if (link.startsWith('/teams')) return '/teams';
    if (link.startsWith('/ticker')) return '/ticker';
    if (link.startsWith('/tournaments')) return '/tournaments';
    if (link.startsWith('/addressbook')) return '/addressbook';
    if (link.startsWith('/invitations')) return '/invitations';
    if (link.startsWith('/users') || link.startsWith('/admin')) return '/users';
    if (link.startsWith('/?reg=') || link === '/') return '/';
    return null;
};

export const ChatProvider = ({ children }) => {
    const { user } = useAuth();
    const [socket, setSocket] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);
    const reconnectTimeout = useRef(null);
    const [pushStatus, setPushStatus] = useState({
        permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
        has_subscription: false,
        subscription_count: 0,
    });

    // Use runtime URL detection instead of build-time env var for WebSocket
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const BACKEND_WS = (() => {
        // Determine protocol based on current page protocol
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host;
        return `${protocol}//${host}`;
    })();

    // Derived: system notifications (non-chat) for bell
    const systemNotifications = notifications.filter(n => n.type !== 'chat');
    // Derived: chat notifications for sidebar badge
    const chatNotifications = notifications.filter(n => n.type === 'chat');

    // Derived: unread "news" count per sidebar module (from notification links).
    const moduleUnreadCounts = useMemo(() => {
        const counts = {};
        for (const n of notifications) {
            if (n.is_read || n.type === 'chat') continue;
            const m = linkToModule(n.link);
            if (m) counts[m] = (counts[m] || 0) + 1;
        }
        return counts;
    }, [notifications]);

    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    const refreshPushStatus = async () => {
        try {
            if (!user) return;
            const res = await axios.get(`${BACKEND_URL}/api/chat/push/status`);
            setPushStatus((prev) => ({
                ...prev,
                ...res.data,
                permission: typeof Notification !== 'undefined' ? Notification.permission : prev.permission,
            }));
        } catch (e) {
            console.error('Push status fetch failed', e);
        }
    };

    const setupPushSubscription = async ({ requestPermission = false } = {}) => {
        try {
            if (!user || !('serviceWorker' in navigator) || !('PushManager' in window)) return;

            const registration = await navigator.serviceWorker.register('/push-sw.js');
            let subscription = await registration.pushManager.getSubscription();

            if (!subscription) {
                if (Notification.permission === 'default' && requestPermission) {
                    await Notification.requestPermission();
                }
                if (Notification.permission !== 'granted') return;

                const keyRes = await axios.get(`${BACKEND_URL}/api/chat/push/public-key`);
                const publicKey = keyRes.data?.public_key;
                if (!publicKey) return;

                subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(publicKey),
                });
            }

            if (subscription) {
                await axios.post(`${BACKEND_URL}/api/chat/push/subscribe`, subscription.toJSON());
            }
            await refreshPushStatus();
        } catch (e) {
            console.error('Push setup failed', e);
        }
    };

    const enablePushFromUserAction = async () => {
        await setupPushSubscription({ requestPermission: true });
        return Notification.permission;
    };

    useEffect(() => {
        if (user) {
            connectGlobal();
            fetchNotifications();
            setupPushSubscription({ requestPermission: false });
            refreshPushStatus();
        } else {
            if (socket) {
                socket.close();
                setSocket(null);
            }
        }
        return () => {
            if (socket) socket.close();
            if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
        };
    }, [user]);

    // Update derived counts when notifications change
    useEffect(() => {
        setUnreadCount(notifications.filter(n => !n.is_read && n.type !== 'chat').length);
        setUnreadMessageCount(notifications.filter(n => !n.is_read && n.type === 'chat').length);
    }, [notifications]);

    // Poll notifications periodically so the bell updates reliably even when the
    // WebSocket push is dropped (e.g. multi-worker/pod production where the socket
    // is held by a different backend process than the one creating the notification).
    useEffect(() => {
        if (!user) return;
        const id = setInterval(() => { fetchNotifications(); }, 30000);
        const onVisible = () => { if (document.visibilityState === 'visible') fetchNotifications(); };
        document.addEventListener('visibilitychange', onVisible);
        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [user]);

    const fetchNotifications = async () => {
        try {
            const res = await axios.get(`${BACKEND_URL}/api/chat/notifications`);
            setNotifications(res.data);
        } catch (e) {
            console.error("Failed to fetch notifications", e);
        }
    };

    const connectGlobal = () => {
        if (socket && socket.readyState === WebSocket.OPEN) return;

        const ws = new WebSocket(`${BACKEND_WS}/api/chat/ws/global/${user.id}`);

        ws.onopen = () => {
            console.log("Global WS Connected");
        };

        ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data);
                if (msg.type === 'notification') {
                    handleNewNotification(msg.data);
                }
            } catch (e) {
                console.error("WS Parse Error", e);
            }
        };

        ws.onclose = () => {
            console.log("Global WS Closed");
            // Reconnect logic
            reconnectTimeout.current = setTimeout(connectGlobal, 3000);
        };

        setSocket(ws);
    };

    const handleNewNotification = (note) => {
        setNotifications(prev => [note, ...prev]);
        // Only show toast for system notifications, not chat messages
        if (note.type !== 'chat') {
            toast.info(note.title, { description: note.message });
        }
    };

    const markRead = async (id) => {
        try {
            await axios.put(`${BACKEND_URL}/api/chat/notifications/${id}/read`);
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
        } catch (e) {
            console.error("Failed to mark read", e);
        }
    };

    const markAllRead = async () => {
        try {
            await axios.put(`${BACKEND_URL}/api/chat/notifications/read-all`);
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        } catch (e) {
            console.error("Failed to mark all read", e);
        }
    };

    // Mark all unread notifications belonging to a module as read (clears the
    // module's sidebar badge when the user opens that module).
    const markModuleRead = async (modulePath) => {
        const ids = notifications
            .filter(n => !n.is_read && n.type !== 'chat' && linkToModule(n.link) === modulePath)
            .map(n => n.id);
        if (ids.length === 0) return;
        try {
            await axios.post(`${BACKEND_URL}/api/chat/notifications/read-bulk`, ids);
            setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, is_read: true } : n));
        } catch (e) {
            console.error("Failed to mark module read", e);
        }
    };

    const deleteNotification = async (id) => {
        try {
            await axios.delete(`${BACKEND_URL}/api/chat/notifications/${id}`);
            setNotifications(prev => prev.filter(n => n.id !== id));
        } catch (e) {
            console.error("Failed to delete notification", e);
            throw e;
        }
    };

    const bulkDeleteNotifications = async (ids) => {
        try {
            await axios.post(`${BACKEND_URL}/api/chat/notifications/bulk-delete`, ids);
            setNotifications(prev => prev.filter(n => !ids.includes(n.id)));
        } catch (e) {
            console.error("Failed to bulk delete notifications", e);
            throw e;
        }
    };

    return (
        <ChatContext.Provider value={{ 
            notifications, systemNotifications, chatNotifications,
            unreadCount, unreadMessageCount, moduleUnreadCounts, markModuleRead,
            markRead, markAllRead, deleteNotification, bulkDeleteNotifications, 
            setNotifications, setUnreadCount,
            pushStatus, refreshPushStatus, enablePushFromUserAction,
        }}>
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = () => useContext(ChatContext);
